import { toDateKey, isInRange, percentChange, getPreviousPeriod } from "../reports/crmReportMetrics";
import { snapshotAtOrBefore } from "./pipelineSnapshotUtils";
import {
  labelOrigenOportunidad,
  labelTipoOportunidad,
  labelEtapaExpansion,
  ETAPAS_EXPANSION,
} from "./expansionConstants";
import { leadEsCliente } from "./expansionMetrics";

const money = (v) => Math.round((Number(v) || 0) * 100) / 100;
const TERMINAL = new Set(["ganada", "perdida", "pospuesta"]);

function oppCreatedInRange(opp, from, to) {
  return isInRange(opp.creadoEn, from, to);
}

function closureEventsInRange(opp, from, to) {
  const events = (opp.historialEtapas || []).filter(
    (e) => TERMINAL.has(e.etapaNueva) && isInRange(e.fechaHora, from, to)
  );
  if (!events.length) return [];
  const last = events.sort((a, b) => String(b.fechaHora).localeCompare(String(a.fechaHora)))[0];
  return [last];
}

function montoAtEvent(event, opp) {
  if (event?.montoEstimado != null && Number(event.montoEstimado) > 0) {
    return money(event.montoEstimado);
  }
  const n = Number(opp.montoEstimado);
  return Number.isFinite(n) && n > 0 ? money(n) : null;
}

function cycleDays(opp, close) {
  const created = toDateKey(opp.creadoEn);
  if (!created || !close) return null;
  const end = toDateKey(close.fechaHora);
  if (!end) return null;
  const d0 = new Date(created);
  const d1 = new Date(end);
  return Math.max(0, Math.round((d1 - d0) / 86400000));
}

export function expansionFlowMetrics(store, from, to) {
  const opps = store.oportunidadesExpansion || [];
  const ventas = (store.ventasActivas || store.ventas || []).filter((v) => !v.anulada);

  let creadas = 0;
  let valorCreado = 0;
  let ganadas = 0;
  let perdidas = 0;
  let pospuestas = 0;
  let valorEstimadoGanado = 0;
  let cierresSinMonto = 0;
  let ganadasSinVenta = 0;
  const cycles = [];

  opps.forEach((opp) => {
    if (oppCreatedInRange(opp, from, to)) {
      creadas += 1;
      const n = Number(opp.montoEstimado);
      if (Number.isFinite(n) && n > 0) valorCreado += n;
    }
    closureEventsInRange(opp, from, to).forEach((ev) => {
      if (ev.etapaNueva === "ganada") {
        const linked = ventas.some(v => v.opportunityId === opp.id);
        if (!linked) { ganadasSinVenta += 1; return; }
        ganadas += 1;
        const m = montoAtEvent(ev, opp);
        if (m != null) valorEstimadoGanado += m;
        else cierresSinMonto += 1;
        const c = cycleDays(opp, ev);
        if (c != null) cycles.push(c);
      } else if (ev.etapaNueva === "perdida") perdidas += 1;
      else if (ev.etapaNueva === "pospuesta") pospuestas += 1;
    });
  });

  const revenueExpansion = money(
    ventas
      .filter((v) => v.opportunityId && isInRange(v.fecha, from, to))
      .reduce((s, v) => s + money(v.total), 0)
  );
  const revenueTotal = money(
    ventas.filter((v) => isInRange(v.fecha, from, to)).reduce((s, v) => s + money(v.total), 0)
  );

  const winDenom = ganadas + perdidas;
  const winRateCount = winDenom ? Math.round((ganadas / winDenom) * 1000) / 10 : null;

  cycles.sort((a, b) => a - b);
  const cicloMediana = cycles.length
    ? (cycles[Math.floor((cycles.length - 1) / 2)] + cycles[Math.floor(cycles.length / 2)]) / 2
    : null;
  const cicloPromedio = cycles.length
    ? Math.round((cycles.reduce((s, x) => s + x, 0) / cycles.length) * 10) / 10
    : null;

  const ventasClientesSinOpp = ventas.filter((v) => {
    if (!isInRange(v.fecha, from, to) || v.opportunityId) return false;
    return v.leadId && leadEsCliente(store.ventas || [], v.leadId);
  }).length;

  return {
    creadas,
    valorCreado: money(valorCreado),
    ganadas,
    perdidas,
    pospuestas,
    valorEstimadoGanado: money(valorEstimadoGanado),
    revenueExpansion,
    revenueTotal,
    participacionExpansion: revenueTotal > 0
      ? Math.round((revenueExpansion / revenueTotal) * 1000) / 10
      : null,
    winRateCount,
    cicloMediana,
    cicloPromedio,
    cierresSinMonto,
    ganadasSinVenta,
    ventasClientesSinOpp,
  };
}

export function expansionStockAtEnd(store, periodEnd) {
  const snap = snapshotAtOrBefore(store.snapshotsPipeline, periodEnd);
  if (!snap) {
    return {
      snapshotFecha: null,
      pipelineNuevos: null,
      pipelineExpansion: null,
      pipelineTotal: null,
      oportunidadesAbiertas: null,
      sinEstimacion: null,
      seguimientoVencido: null,
      porEtapa: null,
    };
  }
  return {
    snapshotFecha: snap.id,
    pipelineNuevos: snap.pipelineNuevos,
    pipelineExpansion: snap.pipelineExpansion,
    pipelineTotal: snap.pipelineTotal,
    oportunidadesAbiertas: snap.oportunidadesAbiertas,
    sinEstimacion: snap.sinEstimacion,
    seguimientoVencido: snap.seguimientoVencido,
    porEtapa: snap.porEtapa,
  };
}

export function buildExpansionComparison(store, from, to, previousRange = null) {
  const previous = previousRange || getPreviousPeriod(from, to);
  const pf = previous.from, pt = previous.to;

  const flowCurrent = expansionFlowMetrics(store, from, to);
  const flowPrevious = expansionFlowMetrics(store, pf, pt);
  const stockCurrent = expansionStockAtEnd(store, to);
  const stockPrevious = expansionStockAtEnd(store, pt);

  const kpi = (key) => ({
    actual: flowCurrent[key],
    anterior: flowPrevious[key],
    variacion: typeof flowCurrent[key] === "number" && typeof flowPrevious[key] === "number"
      ? percentChange(flowCurrent[key], flowPrevious[key])
      : null,
  });

  const snapshots = store.snapshotsPipeline || [];
  const firstSnapshot = snapshots.length ? [...snapshots].sort((a, b) => a.id.localeCompare(b.id))[0]?.id : null;

  const pipelineSeries = pipelineEvolutionSeries(snapshots, from, to);
  const stageCompare = expansionStageCompare(stockCurrent, stockPrevious);

  return {
    flow: {
      creadas: kpi("creadas"),
      ganadas: kpi("ganadas"),
      perdidas: kpi("perdidas"),
      pospuestas: kpi("pospuestas"),
      valorCreado: kpi("valorCreado"),
      valorEstimadoGanado: kpi("valorEstimadoGanado"),
      revenueExpansion: kpi("revenueExpansion"),
      revenueTotal: kpi("revenueTotal"),
      winRateCount: kpi("winRateCount"),
    },
    stock: { current: stockCurrent, previous: stockPrevious },
    quality: {
      current: {
        cierresSinMonto: flowCurrent.cierresSinMonto,
        ganadasSinVenta: flowCurrent.ganadasSinVenta,
        ventasClientesSinOpp: flowCurrent.ventasClientesSinOpp,
        participacionExpansion: flowCurrent.participacionExpansion,
        cicloMediana: flowCurrent.cicloMediana,
        cicloPromedio: flowCurrent.cicloPromedio,
      },
      previous: {
        cierresSinMonto: flowPrevious.cierresSinMonto,
        ganadasSinVenta: flowPrevious.ganadasSinVenta,
        ventasClientesSinOpp: flowPrevious.ventasClientesSinOpp,
      },
    },
    firstSnapshotDate: firstSnapshot,
    segmentOrigen: segmentBy(store, from, to, "origen", labelOrigenOportunidad),
    segmentTipo: segmentBy(store, from, to, "tipo", labelTipoOportunidad),
    pipelineSeries,
    stageCompare,
  };
}

function segmentBy(store, from, to, field, labelFn) {
  const opps = store.oportunidadesExpansion || [];
  const map = new Map();
  opps.forEach((opp) => {
    const key = opp[field] || "sin_asignar";
    if (!map.has(key)) {
      map.set(key, { nombre: labelFn(key === "sin_asignar" ? "" : key) || key, creadas: 0, ganadas: 0, perdidas: 0 });
    }
    const row = map.get(key);
    if (oppCreatedInRange(opp, from, to)) row.creadas += 1;
    closureEventsInRange(opp, from, to).forEach((ev) => {
      if (ev.etapaNueva === "ganada" && (store.ventasActivas || store.ventas || []).some(v => !v.anulada && v.opportunityId === opp.id)) row.ganadas += 1;
      if (ev.etapaNueva === "perdida") row.perdidas += 1;
    });
  });
  return [...map.values()].sort((a, b) => b.creadas - a.creadas);
}

export function pipelineEvolutionSeries(snapshots, from, to) {
  const list = (snapshots || []).filter((s) => s.id >= from && s.id <= to).sort((a, b) => a.id.localeCompare(b.id));
  return list.map((s) => ({
    fecha: s.id,
    pipelineNuevos: s.pipelineNuevos,
    pipelineExpansion: s.pipelineExpansion,
    pipelineTotal: s.pipelineTotal,
  }));
}

export function expansionStageCompare(stockCurrent, stockPrevious) {
  return ETAPAS_EXPANSION.map((etapa) => ({
    etapa,
    label: labelEtapaExpansion(etapa),
    countActual: stockCurrent?.porEtapa?.[etapa]?.count ?? null,
    montoActual: stockCurrent?.porEtapa?.[etapa]?.monto ?? null,
    countAnterior: stockPrevious?.porEtapa?.[etapa]?.count ?? null,
    montoAnterior: stockPrevious?.porEtapa?.[etapa]?.monto ?? null,
  }));
}

export function buildExpansionOperationalDatasets(store, from, to) {
  const opps = store.oportunidadesExpansion || [];
  const ventas = (store.ventas || []).filter((v) => !v.anulada && v.opportunityId);
  const leads = store.leads || [];
  const name = (id) => leads.find((l) => l.id === id)?.nombre || "";

  const oportunidades = opps
    .filter((o) => oppCreatedInRange(o, from, to) || closureEventsInRange(o, from, to).length)
    .map((o) => ({
      id: o.id,
      leadId: o.leadId,
      cliente: name(o.leadId),
      nombre: o.nombre,
      tipo: labelTipoOportunidad(o.tipo),
      origen: labelOrigenOportunidad(o.origen),
      etapa: labelEtapaExpansion(o.etapa),
      montoEstimado: o.montoEstimado ?? "",
      creadoEn: toDateKey(o.creadoEn),
      cerradaEn: o.cerradaEn ? toDateKey(o.cerradaEn) : "",
    }));

  const movimientos = [];
  opps.forEach((o) => {
    (o.historialEtapas || []).forEach((e, i) => {
      if (!isInRange(e.fechaHora, from, to)) return;
      movimientos.push({
        opportunityId: o.id,
        leadId: o.leadId,
        fecha: toDateKey(e.fechaHora),
        etapaAnterior: e.etapaAnterior || "",
        etapaNueva: e.etapaNueva,
        montoEstimado: e.montoEstimado ?? "",
      });
    });
  });

  const ventasExpansion = ventas
    .filter((v) => isInRange(v.fecha, from, to))
    .map((v) => ({
      ventaId: v.id,
      opportunityId: v.opportunityId,
      leadId: v.leadId,
      cliente: v.nombreCliente || name(v.leadId),
      fecha: toDateKey(v.fecha),
      total: money(v.total),
    }));

  const snapshots = (store.snapshotsPipeline || [])
    .filter((s) => s.id >= from && s.id <= to)
    .map((s) => ({
      fecha: s.id,
      pipelineNuevos: s.pipelineNuevos,
      pipelineExpansion: s.pipelineExpansion,
      pipelineTotal: s.pipelineTotal,
      abiertas: s.oportunidadesAbiertas,
      sinEstimacion: s.sinEstimacion,
      actualizadoEn: s.actualizadoEn,
    }));

  return { oportunidadesExpansion: oportunidades, movimientosExpansion: movimientos, ventasExpansion, snapshotsPipeline: snapshots };
}
